import { useMemo } from 'react';
import { Box, Typography, Stack, Chip } from '@mui/material';
import { friendlyNameConverter } from '../../../../services/localizationService';
import { descriptiveStats, extractValues } from '../statistics';
import { useTimelineControls } from '../TimelineContext';

export default function StatsWidget({ recording, sensorKey }) {
  const { window: timeWindow } = useTimelineControls();
  const sensorName = friendlyNameConverter(sensorKey || '');
  const isWindowed = timeWindow != null;

  const stats = useMemo(() => {
    const values = extractValues(recording, sensorKey, timeWindow?.start, timeWindow?.end);
    return descriptiveStats(values);
  }, [recording, sensorKey, timeWindow]);

  if (!stats) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography color="text.secondary" variant="body2">No data for {sensorName}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1, height: '100%', overflow: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2">{sensorName}</Typography>
        {isWindowed && <Chip label="Windowed" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />}
      </Stack>
      <Stack spacing={0.25}>
        <StatRow label="Min" value={stats.min.toFixed(2)} />
        <StatRow label="Max" value={stats.max.toFixed(2)} />
        <StatRow label="Delta" value={stats.delta.toFixed(2)} />
        <StatRow label="Mean" value={stats.mean.toFixed(2)} />
        <StatRow label="Std Dev" value={stats.stdev.toFixed(2)} />
        <StatRow label="Q1" value={stats.q1.toFixed(2)} />
        <StatRow label="Median" value={stats.median.toFixed(2)} />
        <StatRow label="Q3" value={stats.q3.toFixed(2)} />
        <StatRow label="IQR" value={stats.iqr.toFixed(2)} />
        <StatRow label="Mode" value={stats.mode !== null ? stats.mode.toFixed(2) : '—'} />
        <StatRow label="Count" value={stats.count.toLocaleString()} />
      </Stack>
    </Box>
  );
}

function StatRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="caption" fontWeight="bold">{value}</Typography>
    </Stack>
  );
}
